export type SectionId =
  'addEvents' | 'customize' | 'collaborate' | 'sendToCalendar';

export type StepId =
  | 'magic'
  | 'import'
  | 'switchViews'
  | 'viewSettings'
  | 'style'
  | 'invite'
  | 'send';

import type { CustomizeControlId } from '../CustomizeMode/customizeOptions';

export type AnchorId =
  | 'addEvents'
  | 'magicChat'
  | 'viewPicker'
  | 'customize'
  | `customizeTab:${CustomizeControlId}`
  | 'magicPanel'
  | 'platformBar'
  | 'shareInvite'
  | 'sendToCalendar';

/**
 * What the planner should be showing while a card is up. The card opens it on
 * arrival so the thing being described is actually on screen; `none` leaves the
 * planner alone and the ring carries the attention instead.
 */
export type Surface =
  'none' | 'magic' | 'sources' | 'views' | 'style' | 'share' | 'send';

/**
 * What dims while a beat is up.
 *
 * `spotlight` lights the ring's own box and nothing else, which suits a beat
 * pointing at one button. An edge scrim dims the page past one edge of a
 * control instead, for a beat whose subject is a whole bar or rail: the
 * spotlight would darken the Sources tab's neighbours in the same rail, or the
 * Magic chat the beat just opened. `none` dims nothing, for a beat whose panel
 * is there to be read.
 */
export type Scrim =
  'spotlight' | 'none' | { anchor: AnchorId; from: 'bottom' | 'right' };

/**
 * One stop on a card's path: the control the ring rests on, the state the
 * planner should be in to make it reachable, and what the rest of the page does
 * meanwhile. A beat's surface is applied on arrival, before its anchor can
 * register, since opening the panel is usually what puts that control on screen.
 */
export type Beat = {
  anchor: AnchorId;
  surface?: Surface;
  /** Defaults to `spotlight`. */
  scrim?: Scrim;
};

type StepDefinition = { id: StepId; beats: Beat[] };

type Section = {
  id: SectionId;
  /**
   * A card's path, not a single target. Reaching Sources means pressing
   * Customize and picking Sources from the tab rail, and the ring walks those in
   * turn so the route is shown rather than the panel just appearing.
   */
  steps: StepDefinition[];
};

// Customize is reached from the top chrome, so the whole of it stays lit and
// only the calendar underneath dims. Pointing the spotlight at the Customize
// button alone, then at one tab in the rail, dimmed the bar the card is asking
// them to read.
const BELOW_PLATFORM_BAR = {
  anchor: 'platformBar',
  from: 'bottom',
} as const satisfies Scrim;

// The ring never travels inside a panel: it rests on the control that opens the
// panel, then on the tab that names it, and stops there while the panel itself
// opens undimmed alongside. Customize is two stops, not three: the home screen
// its `customizeHome` surface used to show is a waypoint the author does not
// need to be walked through three times over.
const SECTIONS: Section[] = [
  {
    id: 'addEvents',
    steps: [
      {
        id: 'magic',
        beats: [
          { anchor: 'addEvents' },
          // The Add modal's own overlay already dims the page.
          { anchor: 'magicChat', surface: 'magic', scrim: 'none' },
        ],
      },
      {
        id: 'import',
        beats: [
          // Closes the Add modal the card before it opened: this card is about
          // the top chrome, which the modal would cover.
          {
            anchor: 'customize',
            surface: 'none',
            scrim: BELOW_PLATFORM_BAR,
          },
          // The panel is the point once it is open, so the page comes back up,
          // and the ring hands over to the tab that is now lit in the bar.
          { anchor: 'customizeTab:sources', surface: 'sources', scrim: 'none' },
        ],
      },
    ],
  },
  {
    id: 'customize',
    steps: [
      { id: 'switchViews', beats: [{ anchor: 'viewPicker', surface: 'none' }] },
      {
        id: 'viewSettings',
        beats: [
          { anchor: 'customize', scrim: BELOW_PLATFORM_BAR },
          { anchor: 'customizeTab:views', surface: 'views', scrim: 'none' },
        ],
      },
      {
        id: 'style',
        beats: [
          { anchor: 'customize', scrim: BELOW_PLATFORM_BAR },
          { anchor: 'customizeTab:style', surface: 'style', scrim: 'none' },
        ],
      },
    ],
  },
  {
    id: 'collaborate',
    steps: [
      // The Share modal brings its own overlay, which already dims the page
      // behind it. A spotlight on top of that dimmed the modal's own header and
      // link row, leaving one lit section floating in a greyed-out dialog.
      {
        id: 'invite',
        beats: [{ anchor: 'shareInvite', surface: 'share', scrim: 'none' }],
      },
    ],
  },
  {
    id: 'sendToCalendar',
    steps: [
      { id: 'send', beats: [{ anchor: 'sendToCalendar', surface: 'send' }] },
    ],
  },
];

export type Step = StepDefinition & {
  section: SectionId;
  /** Zero-based position of the owning section, for the four-segment bar. */
  sectionIndex: number;
  /** One-based position within the section, for the "2/3" in the eyebrow. */
  positionInSection: number;
  stepsInSection: number;
};

export const STEPS: Step[] = SECTIONS.flatMap((section, sectionIndex) =>
  section.steps.map((step, index) => ({
    ...step,
    section: section.id,
    sectionIndex,
    positionInSection: index + 1,
    stepsInSection: section.steps.length,
  }))
);

export const SECTION_COUNT = SECTIONS.length;

export const STEP_COUNT = STEPS.length;

export const getStepAt = (index: number): Step | null => STEPS[index] ?? null;
